import React, { useState, useEffect, useMemo, useRef } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import Select from "react-select";
import { AgGridReact } from 'ag-grid-react';
import DatePicker from "react-datepicker";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "react-datepicker/dist/react-datepicker.css";
import redirectURL from "./redirectURL";
import { FaInfoCircle } from "react-icons/fa";

const DemandPlanRunRate = () => {
    const [loadshow, setLoadshow] = useState("show-n");
    const [overlayBlock, setOverlayBlock] = useState("show-n");
    const [overlyBlock, setOverlyBlock] = useState("show-n");
    const [show, setShow] = useState(false);
    const [basicTitle, setBasicTitle] = useState("");
    const [basicType, setBasicType] = useState("default");
    const [filteredData, setFilteredData] = useState([]);

    // Trigger states
    const [isSliderOpen, setSliderOpen] = useState(false);
    const [sliderTitle, setSliderTitle] = useState("");
    const [triggerDate, setTriggerDate] = useState("");
    const [isTriggering, setIsTriggering] = useState(false);

    // Filter states
    const [month, setMonth] = useState({ label: "ALL", value: "" });
    const [warehouse, setWarehouse] = useState({ label: "ALL", value: "" });
    const [monthOptions, setMonthOptions] = useState([{ label: "ALL", value: "" }]);
    const [warehouseOptions, setWarehouseOptions] = useState([{ label: "ALL", value: "" }]);

    const gridApi = useRef(null);

    // Get current date in YYYY-MM-DD format
    const getCurrentDate = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Custom Header Component
    const CustomHeader = (props) => {
        const tooltip = props?.column?.getColDef()?.headerTooltip || "";
        const [showMenu, setShowMenu] = useState(false);
        const [showTooltip, setShowTooltip] = useState(false);

        const onMenuClick = (event) => {
            props.showColumnMenu(event.currentTarget);
        };

        const onSortRequested = (order, event) => {
            props.setSort(order, event?.shiftKey);
        };

        const getSort = () => {
            return props.column.getSort();
        };

        const isSortable = props.column.getColDef().sortable;

        return (
            <div
                style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}
                onMouseEnter={() => setShowMenu(true)}
                onMouseLeave={() => setShowMenu(false)}
            >
                <span
                    style={{
                        cursor: isSortable ? "pointer" : "default",
                        userSelect: "none"
                    }}
                    onClick={isSortable ? () => {
                        const currentSort = getSort();
                        let newSort = 'asc';
                        if (currentSort === 'asc') newSort = 'desc';
                        else if (currentSort === 'desc') newSort = null;
                        onSortRequested(newSort, null);
                    } : undefined}
                >
                    {props.displayName}
                </span>

                {isSortable && (
                    <div style={{ display: "flex", flexDirection: "column", marginRight: 2 }}>
                        <span
                            style={{
                                cursor: "pointer",
                                fontSize: 10,
                                opacity: getSort() === 'asc' ? 1 : 0.3,
                                lineHeight: "8px"
                            }}
                            onClick={() => onSortRequested('asc', null)}
                        >
                            ↑
                        </span>
                        <span
                            style={{
                                cursor: "pointer",
                                fontSize: 10,
                                opacity: getSort() === 'desc' ? 1 : 0.3,
                                lineHeight: "8px"
                            }}
                            onClick={() => onSortRequested('desc', null)}
                        >
                            ↓
                        </span>
                    </div>
                )}

                <div
                    style={{ position: "relative", display: "inline-block" }}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    <FaInfoCircle style={{ fontSize: 18, opacity: 0.7 }} />

                    {showTooltip && tooltip && (
                        <div
                            style={{
                                position: "absolute",
                                top: "100%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                backgroundColor: "rgba(0, 0, 0, 0.8)",
                                color: "white",
                                padding: "8px 12px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                whiteSpace: "nowrap",
                                zIndex: 1000,
                                marginTop: "5px",
                            }}
                        >
                            {tooltip}
                        </div>
                    )}
                </div>

                {props.enableMenu && (
                    <span
                        onClick={onMenuClick}
                        style={{
                            marginLeft: "5px",
                            cursor: "pointer",
                            fontSize: 14,
                            opacity: showMenu ? 1 : 0,
                            transition: "opacity 0.2s ease",
                        }}
                    >
                        ☰
                    </span>
                )}
            </div>
        );
    };

    // Column Definitions
    const columnDefs = [
        {
            headerName: "SKU Code",
            field: "sku_code",
            sortable: true,
            resizable: true,
            width: 120,
        },
        {
            headerName: "Warehouse",
            field: "warehouse",
            sortable: true,
            resizable: true,
            width: 130,
        },
        {
            headerName: "Month",
            field: "month",
            sortable: true,
            resizable: true,
            width: 120,
        },
        {
            headerName: "B2B DP Units",
            field: "b2b_dp_units",
            sortable: true,
            resizable: true,
            hide: true,
            width: 180,
        },
        {
            headerName: "B2C DP Units",
            field: "b2c_dp_units",
            sortable: true,
            hide: true,
            resizable: true,
            width: 180,
        },
        {
            headerName: "B2B Run Rate",
            field: "b2b_runrate",
            sortable: true,
            resizable: true,
            width: 180,
        },
        {
            headerName: "B2C Run Rate",
            field: "b2c_runrate",
            sortable: true,
            resizable: true,
            width: 180,
        },
        {
            headerName: "Updated Date",
            field: "updated_date",
            sortable: true,
            resizable: true,
            hide: true,
            width: 180,
            valueFormatter: (params) => {
                if (params.value) {
                    return new Date(params.value).toLocaleDateString();
                }
                return '';
            },
        }
    ];

    const sideBar = useMemo(() => ({
        toolPanels: [
            {
                id: "columns",
                labelDefault: "Columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
            },
            {
                id: "filters",
                labelDefault: "Filters",
                labelKey: "filters",
                iconKey: "filter",
                toolPanel: "agFiltersToolPanel",
            },
        ]
    }), []);

    const statusPanel = {
        statusPanels: [
            {
                statusPanel: "agTotalAndFilteredRowCountComponent",
                align: "left"
            },
            {
                statusPanel: "agTotalRowCountComponent",
                align: "center"
            },
            { statusPanel: "agFilteredRowCountComponent" },
            { statusPanel: "agSelectedRowCountComponent" },
            { statusPanel: "agAggregationComponent" }
        ]
    };

    const defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 100,
    };

    const onGridReady = (params) => {
        gridApi.current = params.api;

        setTimeout(() => {
            if (gridApi.current) {
                gridApi.current.sizeColumnsToFit();
            }
        }, 100);
    };

    // Get Demand Plan Run Rate Data
    const getDemandPlanData = async () => {
        try {
            setLoadshow("show-m");
            setOverlayBlock("show-m");
            
            await getMonthOptions();
            await getWarehouseOptions();
            await loadData("", "");
            
            setLoadshow("show-n");
            setOverlayBlock("show-n");
        } catch (error) {
            console.error("Error fetching demand plan data:", error);
            setBasicTitle("Failed to fetch demand plan data");
            setBasicType("error");
            setShow(true);
            setLoadshow("show-n");
            setOverlayBlock("show-n");
        }
    };

    const loadData = async (selectedMonth = "", selectedWarehouse = "") => {
        try {
            const params = {
                month: selectedMonth || "",
                warehouse: selectedWarehouse
            };

            const response = await redirectURL.post("inventory/getFGDemandPlanRunRates", params);

            if (response.status === 200) {
                if (response.data.data && response.data.data.length > 0) {
                    setFilteredData(response.data.data);
                    
                    setTimeout(() => {
                        if (gridApi.current) {
                            gridApi.current.sizeColumnsToFit();
                        }
                    }, 100);
                } else {
                    setFilteredData([]);
                    setBasicTitle("No data found for the selected filters");
                    setBasicType("info");
                    setShow(true);
                }
            }
        } catch (error) {
            console.error("Error loading data:", error);
            setFilteredData([]);
            setBasicTitle("Failed to load data");
            setBasicType("error");
            setShow(true);
        }
    };

    const getMonthOptions = async () => {
        try {
            const response = await redirectURL.post("inventory/getDemandPlanMonths", {});

            if (response.status === 200 && response.data.data) {
                const months = response.data.data;
                const options = [
                    { label: "ALL", value: "" },
                    ...months.map(m => ({
                        value: m,
                        label: m
                    }))
                ];
                setMonthOptions(options);
                setMonth(options[0]);
            }
        } catch (error) {
            console.error("Error fetching month options:", error);
            setMonthOptions([{ label: "ALL", value: "" }]);
        }
    };

    const getWarehouseOptions = async () => {
        try {
            const response = await redirectURL.post("inventory/getDemandPlanWarehouses", {});

            if (response.status === 200 && response.data.data) {
                const warehouses = response.data.data;
                const options = [
                    { label: "ALL", value: "" },
                    ...warehouses.map(wh => ({
                        value: wh,
                        label: wh
                    }))
                ];
                setWarehouseOptions(options);
                setWarehouse(options[0]);
            }
        } catch (error) {
            console.error("Error fetching warehouse options:", error);
            setWarehouseOptions([{ label: "ALL", value: "" }]);
        }
    };

    const applyFilters = async () => {
        setLoadshow("show-m");
        setOverlayBlock("show-m");

        try {
            await loadData(month.value, warehouse.value);
        } catch (error) {
            console.error("Error applying filters:", error);
            setBasicTitle("Failed to apply filters");
            setBasicType("error");
            setShow(true);
        } finally {
            setLoadshow("show-n");
            setOverlayBlock("show-n");
        }
    };

    const resetFilters = async () => {
        setLoadshow("show-m");
        setOverlayBlock("show-m");

        try {
            setMonth({ label: "ALL", value: "" });
            setWarehouse({ label: "ALL", value: "" });
            await loadData("", "");
        } catch (error) {
            console.error("Error resetting filters:", error);
            setBasicTitle("Failed to reset filters");
            setBasicType("error");
            setShow(true);
        } finally {
            setLoadshow("show-n");
            setOverlayBlock("show-n");
        }
    };

    // Trigger Functionality
    const onTriggerDemandPlan = async () => {
        if (!triggerDate) {
            setShow(true);
            setBasicType("info");
            setBasicTitle("Please select a date");
            return;
        }

        try {
            setIsTriggering(true);
            setLoadshow("show-m");
            setOverlayBlock("show-m");

            const response = await redirectURL.post(
                "inventory/onTriggerFGDemandPlanRunRates",
                { date: triggerDate }
            );

            const res = response?.data?.pyResponse;

            if (res?.status === "Success") {
                setLoadshow("show-n");
                setOverlayBlock("show-n");
                setShow(true);
                setBasicType("success");
                setBasicTitle(res.message || "Demand Plan Run Rates triggered successfully");
                
                // Close slider
                hideOverlySlideBlock();
                
                // Trigger user action log
                const userData = {
                    email: localStorage.getItem("email"),
                    user_code: localStorage.getItem("user_code"),
                    action: "Triggered Demand Plan Run Rates",
                    status: "Success",
                    date: triggerDate,
                    path: window.location.pathname
                };
                triggerUserAction(userData);

                // Refresh the data after successful trigger
                await getDemandPlanData();
            } else {
                setLoadshow("show-n");
                setOverlayBlock("show-n");
                setShow(true);
                setBasicType("info");
                setBasicTitle(res?.message || "Failed to trigger Demand Plan Run Rates");

                // User action log for failure
                const userData = {
                    email: localStorage.getItem("email"),
                    user_code: localStorage.getItem("user_code"),
                    action: "Triggered Demand Plan Run Rates",
                    status: "Failure",
                    date: triggerDate,
                    path: window.location.pathname
                };
                triggerUserAction(userData);
            }

        } catch (error) {
            console.error("Error triggering Demand Plan Run Rates:", error);
            setLoadshow("show-n");
            setOverlayBlock("show-n");
            setShow(true);
            setBasicType("error");
            setBasicTitle(
                error?.response?.data?.message || "Failed to trigger Demand Plan Run Rates. Please try again later."
            );
            
            const userData = {
                email: localStorage.getItem("email"),
                user_code: localStorage.getItem("user_code"),
                action: "Triggered Demand Plan Run Rates",
                status: "Failed",
                date: triggerDate,
                path: window.location.pathname
            };
            triggerUserAction(userData);
        } finally {
            setIsTriggering(false);
        }
    };

    // Open Trigger Slider
    const onClickTriggerDemandPlan = () => {
        // Set current date as default
        setTriggerDate(getCurrentDate());
        setSliderOpen(true);
        setSliderTitle("Trigger Demand Plan Run Rates");
        setOverlyBlock("show-m");
    };

    // Hide Slider
    const hideOverlySlideBlock = () => {
        setOverlyBlock("show-n");
        setSliderOpen(false);
    };

    // Close Slider Icon
    const onClickingSliderCloseIcon = () => {
        setOverlyBlock("show-n");
        setSliderOpen(false);
    };

    // User Action Triggering
    const triggerUserAction = (trigerdata) => {
        redirectURL.post('inventory/triggerUserAction', trigerdata)
    };

    useEffect(() => {
        getDemandPlanData();
    }, []);

    const closeAlert = () => {
        setShow(false);
        setLoadshow("show-n");
        setOverlayBlock("show-n");
    };

    return (
        <>
            <div className="container-fluid mt-3">
                <div style={{ zIndex: "9999999" }}>
                    <SweetAlert
                        show={show}
                        type={basicType}
                        title={basicTitle}
                        onConfirm={closeAlert}
                        style={{ fontSize: "9px" }}
                    />
                </div>

                <div className="row" style={{ fontSize: "0.9rem", fontFamily: "Arial, sans-serif" }}>
                    
                    <form className="col-sm-12 p-0 mt-4 mb-4" style={{ display: "flex", alignItems: "center" }}>
                        <div className="col-sm-10 row">
                            {/* Month Filter */}
                            <div className="col-sm-2 mb-1" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label htmlFor="month" style={{ fontSize: '13px', fontWeight: '400' }}>
                                    Month:
                                </label>
                                <Select
                                    id="month"
                                    value={month}
                                    onChange={(selectedOption) => setMonth(selectedOption)}
                                    options={monthOptions}
                                    styles={selectStyles}
                                />
                            </div>

                            {/* Warehouse Filter */}
                            <div className="col-sm-2 mb-1" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label htmlFor="warehouse" style={{ fontSize: '13px', fontWeight: '400' }}>
                                    Warehouse:
                                </label>
                                <Select
                                    id="warehouse"
                                    value={warehouse}
                                    onChange={(selectedOption) => setWarehouse(selectedOption)}
                                    options={warehouseOptions}
                                    styles={selectStyles}
                                />
                            </div>

                            {/* Filter Buttons */}
                            <div className="ml-4 mt-4" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0px 10px" }}>
                                <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); applyFilters(); }}
                                    style={{
                                        backgroundColor: "#93385D",
                                        color: "#fff",
                                        borderRadius: "5px",
                                        padding: "8px 20px",
                                        border: "none",
                                        cursor: "pointer"
                                    }}
                                >
                                    Apply
                                </button>
                                <button
                                    onClick={(e) => { e.preventDefault(); resetFilters(); }}
                                    type="button"
                                    style={{
                                        backgroundColor: "#fff",
                                        color: "#000",
                                        borderRadius: "5px",
                                        padding: "8px 20px",
                                        border: "1px solid #d9d9d9",
                                        cursor: "pointer"
                                    }}
                                >
                                    Reset
                                </button>
                            </div>
                        </div>

                        {/* Trigger Button */}
                        <div className="col-sm-2 d-flex justify-content-end">
                            <button
                                type="button"
                                onClick={onClickTriggerDemandPlan}
                                className="mr-2"
                                style={{
                                    ...addButtonStyle,
                                    backgroundColor: "#F4EBEF",
                                    color: "#93385D",
                                    border: "2px solid #93385D",
                                }}
                            >
                           Trigger DP RunRate    
                                                   </button>
                        </div>
                    </form>

                    {/* Data Grid */}
                    <div className="col-sm-12 p-0 pl-2 mt-6">
                        <div
                            className="ag-theme-alpine"
                            style={{
                                height: 'calc(100vh - 250px)',
                                width: "100%",
                                minHeight: '400px'
                            }}
                        >
                            <AgGridReact
                                rowData={filteredData}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                domLayout="normal"
                                onGridReady={onGridReady}
                                sideBar={sideBar}
                                statusBar={statusPanel}
                                enableRangeSelection={true}
                                suppressColumnVirtualisation={true}
                                ensureDomOrder={true}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Trigger Slider - Simple Date Selection */}
            <div
                className={`stock-status-slider ${isSliderOpen ? "open" : ""} slider-25`}
                style={{ width: "25%" }}
            >
                <div className="slider-header-container d-flex justify-content-between">
                    <p className="m-0p">{sliderTitle}</p>
                    <p
                        className="m-0p"
                        style={{ cursor: "pointer" }}
                        onClick={onClickingSliderCloseIcon}
                    >
                        X&nbsp;&nbsp;&nbsp;
                    </p>
                </div>
                
                <div style={{ display: "flex", flexDirection: "column", height: "100%", overflowY: "auto", padding: 5 }}>
                    <div style={{ flexGrow: 1, overflowY: "auto", padding: "10px" }}>
                        <div className="mb-3" style={{ display: "flex", flexDirection: "column" }}>
                            <label
                                htmlFor="triggerDate"
                                style={{ fontSize: "13px", fontWeight: "400", marginBottom: "5px", display: "block" }}
                            >
                                Date:
                            </label>
                            <DatePicker
                                selected={triggerDate ? new Date(triggerDate) : null}
                                onChange={(date) => setTriggerDate(date ? date.toISOString().split('T')[0] : "")}
                                dateFormat="yyyy-MM-dd"
                                className="form-control"
                                placeholderText="Select Date"
                                minDate={new Date("2020-01-01")}
                                maxDate={new Date()}
                                style={{
                                    padding: "7px",
                                    borderRadius: "5px",
                                    border: "1px solid #d9d9d9",
                                    width: "100%",
                                    marginBottom: "15px",
                                    cursor: "pointer",
                                }}
                            />
                        </div>
                        
                        <div style={{ textAlign: "right", marginTop: "20px" }}>
                            <button
                                onClick={onTriggerDemandPlan}
                                style={{ 
                                    backgroundColor: "#93385D", 
                                    color: "#fff", 
                                    fontSize: "0.8rem",
                                    padding: "8px 20px",
                                    border: "none",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    opacity: isTriggering ? 0.7 : 1
                                }}
                                disabled={isTriggering}
                            >
                                {isTriggering ? "Processing..." : "Trigger DP RunRate"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`loader ${loadshow}`} style={{ zIndex: "1002" }}></div>
            <div className={`overlay-block ${overlayBlock}`}></div>
            <div
                className={"overly-block " + overlyBlock}
                onClick={hideOverlySlideBlock}
            ></div>
        </>
    );
};

// Styles
const selectStyles = {
    control: (base) => ({
        ...base,
        padding: '1px',
        borderRadius: '5px',
        border: '1px solid #d9d9d9',
        minHeight: '38px'
    }),
    singleValue: (base) => ({
        ...base,
        fontWeight: 'normal',
        color: '#333',
        fontSize: "12px"
    }),
    option: (base, state) => ({
        ...base,
        fontSize: "13px",
        fontWeight: state.isSelected ? 'normal' : 'normal',
    })
};

const addButtonStyle = {
    padding: "8px 20px",
    backgroundColor: "#93385D",
    color: "#fff",
    fontFamily: "sans-serif",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "0.8rem",
    fontWeight: "bold",
};

export default DemandPlanRunRate;